import React from 'react';
import { clsx } from 'clsx';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  inputSize?: 'small' | 'medium' | 'large';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth,
      startAdornment,
      endAdornment,
      inputSize = 'medium',
      className,
      ...props
    },
    ref
  ) => {
    const inputClasses = clsx(
      'border rounded transition-all duration-200',
      'focus:outline-none focus:ring-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      {
        // Size variants
        'px-3 py-1.5 text-sm': inputSize === 'small',
        'px-4 py-2 text-base': inputSize === 'medium',
        'px-5 py-3 text-lg': inputSize === 'large',

        // Full width
        'w-full': fullWidth,

        // Error state
        'border-error text-error focus:ring-error-500': error,
        'border-gray-300 text-gray-900 focus:ring-primary-500': !error,

        // Adornments padding
        'pl-10': startAdornment && inputSize === 'small',
        'pl-12': startAdornment && inputSize === 'medium',
        'pl-14': startAdornment && inputSize === 'large',
        'pr-10': endAdornment && inputSize === 'small',
        'pr-12': endAdornment && inputSize === 'medium',
        'pr-14': endAdornment && inputSize === 'large',
      },
      className
    );

    return (
      <div className={clsx('flex flex-col gap-1', { 'w-full': fullWidth })}>
        {label && (
          <label className={clsx('text-sm font-medium', {
            'text-error': error,
            'text-gray-700': !error,
          })}>
            {label}
          </label>
        )}
        
        <div className="relative">
          {startAdornment && (
            <div className={clsx(
              'absolute left-0 top-1/2 -translate-y-1/2 flex items-center pointer-events-none',
              {
                'left-3': inputSize === 'small',
                'left-4': inputSize === 'medium',
                'left-5': inputSize === 'large',
              }
            )}>
              {startAdornment}
            </div>
          )}

          <input
            ref={ref}
            className={inputClasses}
            {...props}
          />

          {endAdornment && (
            <div className={clsx(
              'absolute right-0 top-1/2 -translate-y-1/2 flex items-center',
              {
                'right-3': inputSize === 'small',
                'right-4': inputSize === 'medium',
                'right-5': inputSize === 'large',
              }
            )}>
              {endAdornment}
            </div>
          )}
        </div>

        {helperText && (
          <p className={clsx('text-xs', {
            'text-error': error,
            'text-gray-500': !error,
          })}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

